/**
 * FtpTargetList - Output FTP destinations
 *
 * Features:
 * - Table of all FTP targets
 * - Assignment: per-match OR per-photographer
 * - Create/Edit with server credentials
 * - Test connection functionality
 */

import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchFtpTargets,
  createFtpTarget,
  updateFtpTarget,
  deleteFtpTarget,
  testFtpTarget,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Drawer, Mono } from '../../../../shared/components/redesign';
import styles from './FtpTargetList.module.css';

const ASSIGNMENT_TYPES = [
  { key: 'match', label: 'Per Match', description: 'Assign to specific matches' },
  { key: 'photographer', label: 'Per Photographer', description: 'Default target for photographer' },
  { key: 'default', label: 'Default', description: 'Fallback when no specific assignment' },
];

function FtpTargetDrawer({ target, onSave, onClose, isNew = false }) {
  const [form, setForm] = useState({
    name: target?.name || '',
    host: target?.host || '',
    port: target?.port || 21,
    username: target?.username || '',
    password: target?.password || '',
    remote_path: target?.remote_path || '/',
    assignment_type: target?.assignment_type || 'match',
    enabled: target?.enabled !== false,
    use_sftp: target?.use_sftp || false,
    notes: target?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.host.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save FTP target:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testFtpTarget(form);
      setTestResult({ success: true, message: 'Connection successful!' });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || err.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const body = (
    <>
        <div className={styles.formGroup}>
          <label className={styles.label}>Target Name</label>
          <input
            type="text"
            className={styles.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Getty Images Upload"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Host</label>
            <input
              type="text"
              className={styles.input}
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder="ftp.example.com"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Port</label>
            <input
              type="number"
              className={styles.input}
              value={form.port}
              onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 21 })}
              min={1}
              max={65535}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Username</label>
            <input
              type="text"
              className={styles.input}
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="ftp_user"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Password</label>
            <div className={styles.passwordField}>
              <input
                type={showPassword ? 'text' : 'password'}
                className={styles.input}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="********"
              />
              <button
                type="button"
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Remote Path</label>
          <input
            type="text"
            className={styles.input}
            value={form.remote_path}
            onChange={(e) => setForm({ ...form, remote_path: e.target.value })}
            placeholder="/uploads/"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Assignment Type</label>
          <div className={styles.assignmentOptions}>
            {ASSIGNMENT_TYPES.map((type) => (
              <label
                key={type.key}
                className={clsx(
                  styles.assignmentOption,
                  form.assignment_type === type.key && styles.selected
                )}
              >
                <input
                  type="radio"
                  name="assignment_type"
                  value={type.key}
                  checked={form.assignment_type === type.key}
                  onChange={() => setForm({ ...form, assignment_type: type.key })}
                  className={styles.radio}
                />
                <div>
                  <span className={styles.assignmentLabel}>{type.label}</span>
                  <span className={styles.assignmentDesc}>{type.description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.use_sftp}
                onChange={(e) => setForm({ ...form, use_sftp: e.target.checked })}
                className={styles.checkbox}
              />
              <span>Use SFTP</span>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className={styles.checkbox}
              />
              <span>Active</span>
            </label>
          </div>
        </div>

        <div className={styles.testSection}>
          <Button variant="ghost" onClick={handleTest} disabled={testing || !form.host}>
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          {testResult && (
            <span
              className={clsx(
                styles.testResult,
                testResult.success ? styles.success : styles.error
              )}
            >
              {testResult.message}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.textarea}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes..."
            rows={2}
          />
        </div>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={isNew ? '§ FTP TARGET · NEW' : '§ FTP TARGET'}
      title={isNew ? 'New FTP target' : (target?.name || 'FTP target')}
      ariaLabel={isNew ? 'Create FTP target' : 'Edit FTP target'}
      body={body}
      footer={(
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <span className={styles.footerSpacer} />
          <Btn
            kind="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.host.trim()}
          >
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </Btn>
        </>
      )}
    />
  );
}

export default function FtpTargetList() {
  // Parse query params from hash (e.g., company/ftp-targets?action=new)
  const getHashParams = () => new URLSearchParams(window.location.hash.split('?')[1] || '');

  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modal state
  const [showModal, setShowModal] = useState(
    getHashParams().get('action') === 'new'
  );
  const [editingTarget, setEditingTarget] = useState(null);
  const [isNewTarget, setIsNewTarget] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFtpTargets({ limit: 200 });
      setTargets(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTargets = targets.filter((t) => {
    if (search.trim()) {
      const term = search.toLowerCase();
      if (
        !t.name?.toLowerCase().includes(term) &&
        !t.host?.toLowerCase().includes(term)
      ) {
        return false;
      }
    }

    if (typeFilter !== 'all' && t.assignment_type !== typeFilter) {
      return false;
    }

    return true;
  });

  const handleCreate = () => {
    setEditingTarget(null);
    setIsNewTarget(true);
    setShowModal(true);
  };

  const handleEdit = (target) => {
    setEditingTarget(target);
    setIsNewTarget(false);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    if (isNewTarget) {
      await createFtpTarget(formData);
    } else if (editingTarget) {
      await updateFtpTarget(editingTarget.id, formData);
    }
    loadData();
  };

  const handleDelete = async (target) => {
    if (!confirm(`Delete FTP target "${target.name}"? This cannot be undone.`)) return;
    try {
      await deleteFtpTarget(target.id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (loading && targets.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading FTP targets...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>FTP Targets</h2>
          <p className={styles.subtitle}>
            Output destinations for processed photos
          </p>
        </div>
        <Btn kind="primary" size="sm" onClick={handleCreate}>
          + New target
        </Btn>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search targets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="match">Per Match</option>
          <option value="photographer">Per Photographer</option>
          <option value="default">Default</option>
        </select>

        <span className={styles.count}>{filteredTargets.length} targets</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Host</th>
              <th className={styles.th}>Path</th>
              <th className={styles.th}>Type</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTargets.map((target) => (
              <tr key={target.id} className={styles.row}>
                <td className={styles.td}>
                  <span className={styles.targetName}>{target.name}</span>
                  {target.use_sftp && (
                    <span className={styles.sftpBadge}>SFTP</span>
                  )}
                </td>
                <td className={styles.td}>
                  <code className={styles.host}>
                    {target.host}:{target.port}
                  </code>
                </td>
                <td className={styles.td}>
                  <code className={styles.path}>{target.remote_path}</code>
                </td>
                <td className={styles.td}>
                  <span className={styles.typeBadge}>
                    {ASSIGNMENT_TYPES.find((t) => t.key === target.assignment_type)?.label ||
                      target.assignment_type}
                  </span>
                </td>
                <td className={styles.td}>
                  <span
                    className={clsx(
                      styles.statusBadge,
                      target.enabled ? styles.active : styles.inactive
                    )}
                  >
                    {target.enabled ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <Btn kind="ghost" size="sm" onClick={() => handleEdit(target)}>
                      Edit
                    </Btn>
                    <Btn kind="danger" size="sm" onClick={() => handleDelete(target)}>
                      Delete
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filteredTargets.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No FTP targets found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Editor drawer */}
      {showModal && (
        <FtpTargetDrawer
          target={editingTarget}
          isNew={isNewTarget}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTarget(null);
          }}
        />
      )}
    </div>
  );
}
